import { IExploreCategoryListInput } from "types/api/explore";
import {
  cetitficateParams,
  IBuyCreditOutput,
  ICommentProduct,
  ICourseUser,
  ICourseUserOutput,
  ICreditTransactionUser,
  IMyProductUser,
  IOrdersUser,
  IPasswordUserOutput,
  IProvincesListInput,
  IUserCertificates,
  IUserCoupon,
  IUserInterest,
  IUserInterestRecommended,
  IUserProfileDataInput,
  IUserSaveOutput,
  IUserStates,
  OrdersParams,
  OUserCreditOutput,
} from "../types";
import { IHowRecommendedCourseInput } from "types/api/recommendedCourse";
import gate from "gate/gate";
import client from "@https/api";
import clientV2 from "@https/api-v2";

const userApi = {
  getProfile: (additional: boolean) =>
    gate.get<IUserProfileDataInput, never, { additional: boolean }>({
      client,
      url: "/user/profile",
      ...(additional && { params: { additional: additional } }),
    }),
  getCoupons: () =>
    gate.get<IUserCoupon, never, never>({
      client,
      url: "/coupons",
    }),
  getOrders: (params: OrdersParams) =>
    gate.get<IOrdersUser, never, OrdersParams>({
      client: clientV2,
      url: "order/user/history",
      params: params,
    }),
  getCreditTransactions: () =>
    gate.get<ICreditTransactionUser, never, never>({
      client,
      url: "/user/profile/credit/transaction",
    }),
  cancelOrder: (id: number) =>
    gate.post<{ status: boolean; message: string }, never, never>({
      client,
      url: `/order/cancel/${id}`,
    }),
  getVotiveProducts: (params: { page: number; limit: number }) =>
    gate.get<ICourseUser, never, { page: number; limit: number }>({
      client,
      url: "/nazri-orders",
      params: {
        ...params,
      },
    }),
  getProducts: (params?: ICourseUserOutput) =>
    gate.get<IMyProductUser, never, ICourseUserOutput>({
      client,
      url: "/user/profile/products",
      params: params,
    }),
  getCertificates: (params: cetitficateParams) =>
    gate.get<IUserCertificates, never, cetitficateParams>({
      client,
      url: "/user/profile/certificates",
      params,
    }),
  getProductsWithPendingCertifcate: (params: cetitficateParams) =>
    gate.get<IMyProductUser, never, cetitficateParams>({
      client,
      url: "/user/profile/products/nocertificate",
      params,
    }),
  getWishlist: (params?: { recache?: 1 | 0 }) =>
    gate.get<IHowRecommendedCourseInput, never, { recache?: 1 | 0 }>({
      client,
      url: "/user/profile/favorite",
      params: params,
    }),
  updatePassword: (data: IPasswordUserOutput) =>
    gate.patch<
      { status: boolean; message: string },
      IPasswordUserOutput,
      never
    >({
      client,
      url: "/user/profile/password/update",
      data: data,
    }),
  verifyMobileEmail: (data: { type: "email" | "mobile"; value?: string }) =>
    gate.post<
      { status: boolean; message: string },
      { type: string; value?: string },
      never
    >({
      client,
      url: "/user/profile/confirmation/request",
      data: data,
    }),
  buyCredit: (data: {
    credit: number;
    callback_success: null;
    callback_failed: null;
  }) =>
    gate.post<IBuyCreditOutput, OUserCreditOutput, never>({
      client,
      url: `/order/credit`,
      data,
    }),
  getCategories: () =>
    gate.get<IExploreCategoryListInput, never, never>({
      client,
      url: "/categories?page=1&limit=24",
    }),
  getProvinces: () =>
    gate.get<IProvincesListInput, never, never>({
      client,
      url: "/provinces?with_cities=true",
    }),
  verifyCode: (data: {
    code?: string;
    type: "email" | "mobile";
    token?: string;
    email?: string;
    mobile?: string;
  }) =>
    gate.post<{ status: boolean; message: string }, { code?: string }, never>({
      client,
      url: "/user/profile/confirmation/verify",
      data: data,
    }),
  updateProfile: (data: IUserSaveOutput) =>
    gate.patch<{ status: boolean; message: string }, IUserSaveOutput, never>({
      client,
      url: "/user/profile",
      data: data,
    }),
  getProductComment: () =>
    gate.get<ICommentProduct, never, never>({
      client,
      url: "/user/profile/products/comments",
    }),
  getUserInterest: () =>
    gate.get<IUserInterest, never, never>({
      client,
      url: "/user/profile/interests/me",
    }),
  getUserInterestRecommended: () =>
    gate.get<IUserInterestRecommended, never, never>({
      client,
      url: "/user/profile/interests/all",
    }),
  getUserInterestSearch: (params: { query: string }) =>
    gate.get<IUserInterestRecommended, never, { query: string }>({
      client,
      url: "/user/profile/interests/search",
      params: params,
    }),

  updateUserInterest: (data: { id: number }) =>
    gate.post<IUserInterest, { id: number }>({
      client,
      url: "/user/profile/interests/sync",
      data: data,
    }),
  getUserStates: (params: {
    certificate_share_ab_test_group_name?: "A" | "B";
    user_profile_favorite_subject_is_viewed?: boolean;
  }) =>
    gate.get<IUserStates, never, any>({
      client,
      url: `/user/profile/states`,
      params: params,
    }),
  setUserStates: (data: any) =>
    gate.get<IUserStates, any>({
      client,
      url: `/user/profile/states/create`,
      data: data,
    }),
};

export default userApi;
